import React from 'react';
import { CalendarCheck } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { FenetreEmailsModule } from '../ui/LigneNotification';
import { Segmented } from '../ui/Field';
import { NotificationBlock, insertNotificationToken } from '../PrestationPlanning/PrestationNotificationsPage';

const RDV_PRO_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{lien_annulation}}', label: 'Bouton "Annuler le RDV" (si autorisé sur la démarche)' },
    { token: '{{logo}}', label: 'Votre logo' },
];
const PRO_RDV_REFUSED_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{motif}}', label: 'Motif du refus' },
];
const PRO_RDV_CANCELLED_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{motif}}', label: "Motif de l'annulation" },
    { token: '{{logo}}', label: 'Votre logo' },
];
const PRO_RDV_NEW_REQUEST_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{client_nom}}', label: 'Nom du contact' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
];
const PRO_RDV_ASSIGNMENT_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du salarié' },
    { token: '{{nom}}', label: 'Nom du salarié' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{heure}}', label: 'Heure du rendez-vous' },
    { token: '{{client_nom}}', label: 'Nom du client/contact' },
    { token: '{{raison}}', label: 'Raison du rendez-vous' },
    { token: '{{logo}}', label: 'Votre logo' },
];

/**
 * Réglages des emails du module RDV Pro, ouverts directement depuis le module (bouton « Paramètres »)
 * — même principe que les autres modules migrés. Trois onglets : Client (confirmation/rappel/déplacé/
 * refus), Établissement (annulation reçue, nouvelle demande), Salarié (assignation).
 * Expose au passage deux emails jusqu'ici invisibles dans aucune UI (refus de RDV, nouvelle demande) —
 * le backend les gère depuis longtemps mais rien ne permettait de les personnaliser.
 */
export function RdvProEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [onglet, setOnglet] = React.useState<'client' | 'etablissement' | 'salarie'>('client');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    const [notifyConfirmation, setNotifyConfirmation] = React.useState(true);
    const [confirmationSubject, setConfirmationSubject] = React.useState('');
    const [confirmationBody, setConfirmationBody] = React.useState('');
    const [confirmationActiveField, setConfirmationActiveField] = React.useState<'subject' | 'body'>('body');
    const confirmationSubjectRef = React.useRef<HTMLInputElement>(null);
    const confirmationBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyReminder, setNotifyReminder] = React.useState(true);
    const [reminderSubject, setReminderSubject] = React.useState('');
    const [reminderBody, setReminderBody] = React.useState('');
    const [reminderActiveField, setReminderActiveField] = React.useState<'subject' | 'body'>('body');
    const reminderSubjectRef = React.useRef<HTMLInputElement>(null);
    const reminderBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyReschedule, setNotifyReschedule] = React.useState(true);
    const [rescheduleSubject, setRescheduleSubject] = React.useState('');
    const [rescheduleBody, setRescheduleBody] = React.useState('');
    const [rescheduleActiveField, setRescheduleActiveField] = React.useState<'subject' | 'body'>('body');
    const rescheduleSubjectRef = React.useRef<HTMLInputElement>(null);
    const rescheduleBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyRefused, setNotifyRefused] = React.useState(true);
    const [refusedSubject, setRefusedSubject] = React.useState('');
    const [refusedBody, setRefusedBody] = React.useState('');
    const [refusedActiveField, setRefusedActiveField] = React.useState<'subject' | 'body'>('body');
    const refusedSubjectRef = React.useRef<HTMLInputElement>(null);
    const refusedBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyCancelledByClient, setNotifyCancelledByClient] = React.useState(true);
    const [cancelledSubject, setCancelledSubject] = React.useState('');
    const [cancelledBody, setCancelledBody] = React.useState('');
    const [cancelledActiveField, setCancelledActiveField] = React.useState<'subject' | 'body'>('body');
    const cancelledSubjectRef = React.useRef<HTMLInputElement>(null);
    const cancelledBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyNewRequest, setNotifyNewRequest] = React.useState(true);
    const [newRequestSubject, setNewRequestSubject] = React.useState('');
    const [newRequestBody, setNewRequestBody] = React.useState('');
    const [newRequestActiveField, setNewRequestActiveField] = React.useState<'subject' | 'body'>('body');
    const newRequestSubjectRef = React.useRef<HTMLInputElement>(null);
    const newRequestBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [notifyAssignment, setNotifyAssignment] = React.useState(true);
    const [assignmentSubject, setAssignmentSubject] = React.useState('');
    const [assignmentBody, setAssignmentBody] = React.useState('');
    const [assignmentActiveField, setAssignmentActiveField] = React.useState<'subject' | 'body'>('body');
    const assignmentSubjectRef = React.useRef<HTMLInputElement>(null);
    const assignmentBodyRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getClientNotificationSettings()
            .then((s: any) => {
                setNotifyConfirmation(s ? !!s.notify_pro_rdv_confirmation : true);
                setConfirmationSubject(s?.pro_rdv_confirmation_subject || '');
                setConfirmationBody(s?.pro_rdv_confirmation_body || '');
                setNotifyReminder(s ? !!s.notify_pro_rdv_reminder : true);
                setReminderSubject(s?.pro_rdv_reminder_subject || '');
                setReminderBody(s?.pro_rdv_reminder_body || '');
                setNotifyReschedule(s ? !!s.notify_pro_rdv_reschedule : true);
                setRescheduleSubject(s?.pro_rdv_reschedule_subject || '');
                setRescheduleBody(s?.pro_rdv_reschedule_body || '');
                setNotifyRefused(s ? !!s.notify_pro_rdv_refused : true);
                setRefusedSubject(s?.pro_rdv_refused_subject || '');
                setRefusedBody(s?.pro_rdv_refused_body || '');
                setNotifyCancelledByClient(s ? !!s.notify_pro_rdv_cancelled_by_client : true);
                setCancelledSubject(s?.pro_rdv_cancelled_by_client_subject || '');
                setCancelledBody(s?.pro_rdv_cancelled_by_client_body || '');
                setNotifyNewRequest(s ? !!s.notify_pro_rdv_new_request : true);
                setNewRequestSubject(s?.pro_rdv_new_request_subject || '');
                setNewRequestBody(s?.pro_rdv_new_request_body || '');
                setNotifyAssignment(s ? !!s.notify_pro_rdv_assignment : true);
                setAssignmentSubject(s?.pro_rdv_assignment_subject || '');
                setAssignmentBody(s?.pro_rdv_assignment_body || '');
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const insertConfirmationToken = (t: string) => confirmationActiveField === 'subject'
        ? insertNotificationToken(confirmationSubjectRef.current, confirmationSubject, setConfirmationSubject, t)
        : insertNotificationToken(confirmationBodyRef.current, confirmationBody, setConfirmationBody, t);
    const insertReminderToken = (t: string) => reminderActiveField === 'subject'
        ? insertNotificationToken(reminderSubjectRef.current, reminderSubject, setReminderSubject, t)
        : insertNotificationToken(reminderBodyRef.current, reminderBody, setReminderBody, t);
    const insertRescheduleToken = (t: string) => rescheduleActiveField === 'subject'
        ? insertNotificationToken(rescheduleSubjectRef.current, rescheduleSubject, setRescheduleSubject, t)
        : insertNotificationToken(rescheduleBodyRef.current, rescheduleBody, setRescheduleBody, t);
    const insertRefusedToken = (t: string) => refusedActiveField === 'subject'
        ? insertNotificationToken(refusedSubjectRef.current, refusedSubject, setRefusedSubject, t)
        : insertNotificationToken(refusedBodyRef.current, refusedBody, setRefusedBody, t);
    const insertCancelledToken = (t: string) => cancelledActiveField === 'subject'
        ? insertNotificationToken(cancelledSubjectRef.current, cancelledSubject, setCancelledSubject, t)
        : insertNotificationToken(cancelledBodyRef.current, cancelledBody, setCancelledBody, t);
    const insertNewRequestToken = (t: string) => newRequestActiveField === 'subject'
        ? insertNotificationToken(newRequestSubjectRef.current, newRequestSubject, setNewRequestSubject, t)
        : insertNotificationToken(newRequestBodyRef.current, newRequestBody, setNewRequestBody, t);
    const insertAssignmentToken = (t: string) => assignmentActiveField === 'subject'
        ? insertNotificationToken(assignmentSubjectRef.current, assignmentSubject, setAssignmentSubject, t)
        : insertNotificationToken(assignmentBodyRef.current, assignmentBody, setAssignmentBody, t);

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const res: any = await moduleApi.patchClientNotificationSettings('rdv_pro', {
                notify_pro_rdv_confirmation: notifyConfirmation, pro_rdv_confirmation_subject: confirmationSubject, pro_rdv_confirmation_body: confirmationBody,
                notify_pro_rdv_reminder: notifyReminder, pro_rdv_reminder_subject: reminderSubject, pro_rdv_reminder_body: reminderBody,
                notify_pro_rdv_reschedule: notifyReschedule, pro_rdv_reschedule_subject: rescheduleSubject, pro_rdv_reschedule_body: rescheduleBody,
                notify_pro_rdv_refused: notifyRefused, pro_rdv_refused_subject: refusedSubject, pro_rdv_refused_body: refusedBody,
                notify_pro_rdv_cancelled_by_client: notifyCancelledByClient, pro_rdv_cancelled_by_client_subject: cancelledSubject, pro_rdv_cancelled_by_client_body: cancelledBody,
                notify_pro_rdv_new_request: notifyNewRequest, pro_rdv_new_request_subject: newRequestSubject, pro_rdv_new_request_body: newRequestBody,
                notify_pro_rdv_assignment: notifyAssignment, pro_rdv_assignment_subject: assignmentSubject, pro_rdv_assignment_body: assignmentBody,
            });
            if (res?.error) { setErreur(res.error); return; }
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    return (
        <FenetreEmailsModule titre="Paramètres des emails RDV Pro" icone={<CalendarCheck size={16} />} onClose={onClose}
            chargement={loading} erreur={erreur} enregistrer={enregistrer}
            etat={saving ? 'en_cours' : erreur ? 'echec' : saved ? 'fait' : 'repos'}
            avant={<Segmented value={onglet} options={[{ value: 'client', label: 'Client' }, { value: 'etablissement', label: 'Établissement' }, { value: 'salarie', label: 'Salarié' }]} onChange={(v) => setOnglet(v as typeof onglet)} ariaLabel="Destinataire des emails" />}>
            {onglet === 'client' && (
                <>
                <NotificationBlock title="Confirmation de rendez-vous" description="Part au contact lorsqu'il prend rendez-vous depuis une Démarche client."
                    toggle={{ checked: notifyConfirmation, onChange: setNotifyConfirmation }}
                    subject={confirmationSubject} setSubject={setConfirmationSubject} body={confirmationBody} setBody={setConfirmationBody}
                    subjectRef={confirmationSubjectRef} bodyRef={confirmationBodyRef}
                    activeField={confirmationActiveField} setActiveField={setConfirmationActiveField}
                    onInsertToken={insertConfirmationToken} tokens={RDV_PRO_TOKENS} />
                <NotificationBlock title="Rappel de rendez-vous" description="Part quand vous cliquez sur « Rappeler le RDV »."
                    toggle={{ checked: notifyReminder, onChange: setNotifyReminder }}
                    subject={reminderSubject} setSubject={setReminderSubject} body={reminderBody} setBody={setReminderBody}
                    subjectRef={reminderSubjectRef} bodyRef={reminderBodyRef}
                    activeField={reminderActiveField} setActiveField={setReminderActiveField}
                    onInsertToken={insertReminderToken} tokens={RDV_PRO_TOKENS} />
                <NotificationBlock title="Rendez-vous déplacé" description="Part au contact quand vous cochez la case lors de la modification du rendez-vous."
                    toggle={{ checked: notifyReschedule, onChange: setNotifyReschedule }}
                    subject={rescheduleSubject} setSubject={setRescheduleSubject} body={rescheduleBody} setBody={setRescheduleBody}
                    subjectRef={rescheduleSubjectRef} bodyRef={rescheduleBodyRef}
                    activeField={rescheduleActiveField} setActiveField={setRescheduleActiveField}
                    onInsertToken={insertRescheduleToken} tokens={RDV_PRO_TOKENS} />
                <NotificationBlock title="Refus de la demande" description="Part au contact quand vous refusez sa demande, avec le motif saisi."
                    toggle={{ checked: notifyRefused, onChange: setNotifyRefused }}
                    subject={refusedSubject} setSubject={setRefusedSubject} body={refusedBody} setBody={setRefusedBody}
                    subjectRef={refusedSubjectRef} bodyRef={refusedBodyRef}
                    activeField={refusedActiveField} setActiveField={setRefusedActiveField}
                    onInsertToken={insertRefusedToken} tokens={PRO_RDV_REFUSED_TOKENS} />
            </>
            )}
            {onglet === 'etablissement' && (
                <>
                <NotificationBlock title="Annulation par le client" description="Reçu quand le client clique « Annuler le RDV » dans le mail de confirmation et saisit un motif."
                    toggle={{ checked: notifyCancelledByClient, onChange: setNotifyCancelledByClient }}
                    subject={cancelledSubject} setSubject={setCancelledSubject} body={cancelledBody} setBody={setCancelledBody}
                    subjectRef={cancelledSubjectRef} bodyRef={cancelledBodyRef}
                    activeField={cancelledActiveField} setActiveField={setCancelledActiveField}
                    onInsertToken={insertCancelledToken} tokens={PRO_RDV_CANCELLED_TOKENS} />
                <NotificationBlock title="Nouvelle demande de rendez-vous" description="Reçu dès qu'un contact demande un rendez-vous, avant votre acceptation."
                    toggle={{ checked: notifyNewRequest, onChange: setNotifyNewRequest }}
                    subject={newRequestSubject} setSubject={setNewRequestSubject} body={newRequestBody} setBody={setNewRequestBody}
                    subjectRef={newRequestSubjectRef} bodyRef={newRequestBodyRef}
                    activeField={newRequestActiveField} setActiveField={setNewRequestActiveField}
                    onInsertToken={insertNewRequestToken} tokens={PRO_RDV_NEW_REQUEST_TOKENS} />
            </>
            )}
            {onglet === 'salarie' && (
                <>
                <NotificationBlock title="Assignation à un rendez-vous" description="Part au salarié dès qu'il est assigné à un rendez-vous (qui peut l'être : Paramètres > RDV Pro)."
                    toggle={{ checked: notifyAssignment, onChange: setNotifyAssignment }}
                    subject={assignmentSubject} setSubject={setAssignmentSubject} body={assignmentBody} setBody={setAssignmentBody}
                    subjectRef={assignmentSubjectRef} bodyRef={assignmentBodyRef}
                    activeField={assignmentActiveField} setActiveField={setAssignmentActiveField}
                    onInsertToken={insertAssignmentToken} tokens={PRO_RDV_ASSIGNMENT_TOKENS} />
            </>
            )}
        </FenetreEmailsModule>
    );
}
